import { createReducer, on } from '@ngrx/store';
import { cloneDeep, omit } from 'lodash';

import { SearchResult } from '@core/api';
import { ItemUIState, initActions } from '@core/store';
import { Extent } from '@shared/open-layers';

import { leaveMap } from '../actions/map-page.actions';
import * as searchActions from '../actions/search.actions';
import { parseOSGridRef } from '../utils/search.utils';

export const featureKey = 'search';

export interface SearchState {
  phrase: string;
  stadiaMapsResults: ItemUIState<{ [key: string]: SearchResult[] }>;
  landRegistryTitles: ItemUIState<{ [key: string]: SearchResult[] }>;
  what3wordsResults: ItemUIState<{ [key: string]: SearchResult[] }>;
  gridRefResult?: SearchResult;
  selectedResult?: [number, number][];
  selectedLocation?: GeoJSON.Feature[] | Extent;
}

export const initialState: SearchState = {
  phrase: '',
  stadiaMapsResults: { status: 'INIT' },
  landRegistryTitles: { status: 'INIT' },
  what3wordsResults: { status: 'INIT' },
};

export const reducer = createReducer<SearchState>(
  cloneDeep(initialState),
  on(initActions.appCleanup, leaveMap, () => cloneDeep(initialState)),

  on(searchActions.search, (state, { phrase }) => ({
    ...omit(state, ['selectedResult']),
    phrase,
  })),
  on(searchActions.clear, (state) => ({
    ...state,
    phrase: initialState.phrase,
    what3wordsResults: initialState.what3wordsResults,
  })),

  on(searchActions.searchStadiaMaps, (state, { phrase }) => ({
    ...state,
    stadiaMapsResults: {
      ...state.stadiaMapsResults,
      status: phrase && !Object.keys(state?.stadiaMapsResults).includes(phrase) ? 'LOADING' : 'INIT',
    },
  })),
  on(searchActions.searchStadiaMapsSuccess, (state, { phrase, response }) => ({
    ...state,
    stadiaMapsResults: {
      ...state.stadiaMapsResults,
      status: 'READY',
      [phrase]: response,
    },
  })),
  on(searchActions.searchStadiaMapsFail, (state) => ({
    ...state,
    stadiaMapsResults: {
      ...state.stadiaMapsResults,
      status: 'ERROR',
    },
  })),

  on(searchActions.searchLandRegistry, (state, { phrase }) => ({
    ...state,
    landRegistryTitles: {
      ...state.landRegistryTitles,
      status: phrase && !Object.keys(state?.landRegistryTitles).includes(phrase) ? 'LOADING' : 'INIT',
    },
  })),
  on(searchActions.searchLandRegistrySuccess, (state, { phrase, response }) => ({
    ...state,
    landRegistryTitles: {
      ...state.landRegistryTitles,
      status: 'READY',
      [phrase]: response,
    },
  })),
  on(searchActions.searchLandRegistryFail, (state) => ({
    ...state,
    landRegistryTitles: {
      ...state.landRegistryTitles,
      status: 'ERROR',
    },
  })),

  on(searchActions.searchWhat3Words, (state, { phrase }) => ({
    ...state,
    what3wordsResults: {
      ...state.what3wordsResults,
      status: phrase && !Object.keys(state?.what3wordsResults).includes(phrase) ? 'LOADING' : 'INIT',
    },
  })),
  on(searchActions.searchWhat3WordsSuccess, (state, { phrase, response }) => ({
    ...state,
    what3wordsResults: {
      ...state.what3wordsResults,
      status: 'READY',
      [phrase]: response,
    },
  })),
  on(searchActions.searchWhat3WordsFail, (state) => ({
    ...state,
    what3wordsResults: {
      ...state.what3wordsResults,
      status: 'ERROR',
    },
  })),

  on(searchActions.searchGridRef, (state, { phrase }) => ({
    ...state,
    gridRefResult: parseOSGridRef(phrase),
  })),

  on(searchActions.renderLocation, (state, { location }) => ({
    ...state,
    selectedLocation: location,
  }))
);
